import { union } from 'lodash';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useMatch } from 'react-router-dom-v5-compat';
import { ActionType, Row } from 'react-table';

import {
  BULK_DELETE_LIMIT,
  BULK_EXPORT_LIMIT,
  BULK_RESTORE_LIMIT,
} from '~/common/constants/max_lengths';
import { PAGE_SIZE, PAGE_START } from '~/common/constants/page';
import { DocumentsRestoreFilters } from '~/components/DocumentsPage/DocumentsRestore/DocumentRestoreFilters';
import { useLimitExceededModal } from '~/components/DocumentsPage/DocumentsRestore/useLimitExceededModal';
import {
  displayReason,
  getExportDataFromColumns,
} from '~/components/DocumentsPage/DocumentsRestore/utils';
import DeleteDocumentPermanentlyModal from '~/components/Modals/DeleteDocumentPermanentlyModal/DeleteDocumentPermanentlyModal';
import { showToast } from '~/components/Shared/EcToast/showToast';
import FolderEmptyIcon from '~/components/Shared/Icons/FolderEmptyIcon';
import { PersistedTable } from '~/components/Shared/PersistedTable/PersistedTable';
import { User as UserComponent } from '~/components/Shared/User/User';
import { CheckboxGroup } from '~/eds/src/components/CheckboxGroup/CheckboxGroup';
import { ContentContainer } from '~/eds/src/components/ContentContainer/ContentContainer';
import { useModal } from '~/eds/src/components/hooks/useModal';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Text } from '~/eds/src/components/Text/Text';
import { PageLayout } from '~/eds/src/modules/PageLayout/PageLayout';
import { exportXlsx } from '~/eds/src/utils/exportXlsx';
import { TableContextType } from '~/enums/TableContextType';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { withCurrentUser } from '~/hocs/withCurrentUser';
import { withPermissions } from '~/hocs/withPermissions';
import { useTableSettings } from '~/hooks/useTableSettings';
import { api } from '~/redux/api/createApi';
import {
  DeletedDocumentResult,
  downloadDeletedDocument,
  downloadDeletedDocumentForCurrentUser,
  getDeletedDocuments,
  getDeletedDocumentsForCurrentUser,
} from '~/redux/api/methods/deletedDocument';
import { slices } from '~/redux/slices';
import { RoutePathType } from '~/routing/enums';
import { generateNav } from '~/routing/nav';
import { useRouting } from '~/routing/useRouting';
import { TableQuery } from '~/types/DocumentRestore';
import { ERROR, SUCCESS } from '~/types/toast.types';
import { User } from '~/types/User';
import Alert from '~/ui/components/Alert/Alert';
import FolderModal from '~/ui/components/FolderModal/FolderModal';
import { downloadFile } from '~/utils/files';
import { sortStrings } from '~/utils/sortStrings';

const PAGE_SIZE_LIST = [10, 20, 30, 50, 100]; // TODO: source from EDS

const DEFAULT_SORT = { id: 'deleted_date', desc: true };

const PERMISSIONS = [
  {
    resourceId: 'deleted_documents',
    resourceType: 'view',
  },
];

const Page = ({
  currentUser,
  checkPermission,
}: {
  currentUser: User;
  checkPermission: (resourceId: string, resourceType: string) => boolean;
  isPermissionsLoading: boolean;
}) => {
  const { params } = useRouting();
  const { clientId } = params;
  const { toast } = useToast();
  const intl = useIntl();
  const isAdminConsole = Boolean(
    useMatch(RoutePathType.AdminConsoleClientRestorationHub),
  );

  const isRestorationHub500PageSizeEnabled = useFlag(
    FlagType.RestorationHub500PageSize,
  );

  const initialDeleteRequired = !!currentUser?.client_config
    ?.document_delete_reason_required
    ? ['reason_required']
    : [];

  const RESTORATION_HUB_PAGE_SIZE_LIST = isRestorationHub500PageSizeEnabled
    ? [10, 20, 30, 50, 100, 500]
    : PAGE_SIZE_LIST;

  const dispatch = useDispatch();

  const restorationHubColumnOrder = useSelector(
    slices.restorationHub.selectors.selectColumnOrder,
  );
  const myDeletedItemsColumnOrder = useSelector(
    slices.myDeletedItems.selectors.selectColumnOrder,
  );

  const columnOrder = isAdminConsole
    ? restorationHubColumnOrder
    : myDeletedItemsColumnOrder;

  const [query, setQuery] = useState<TableQuery>({
    page: PAGE_START,
    pageSize: PAGE_SIZE,
    sort: DEFAULT_SORT,
    filters: {},
  });

  const [documentRestoreMode, setDocumentRestoreMode] = useState<string[]>(
    initialDeleteRequired,
  );

  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [showPermanentDeleteModal, setShowPermanentDeleteModal] =
    useState(false);
  const [selectedDocuments, setSelectedDocuments] = useState<
    DeletedDocumentResult[]
  >([]);
  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);
  const [selectedFolder, setSelectedFolder] = useState(0);
  const [allSelected, setAllSelected] = useState(false);

  const getDeletedDocumentsEndpoint = isAdminConsole
    ? api.endpoints.getDeletedDocuments
    : api.endpoints.getDeletedDocumentsForCurrentUser;
  const {
    data: tableData,
    error,
    isFetching: isLoading,
  } = getDeletedDocumentsEndpoint.useQuery(query);
  const { data: folderData, error: folderError } =
    api.useGetFolderTreeQuery(null);

  const [updateClientAdmin, { isLoading: isUpdatingSettings }] =
    api.endpoints.updateClientAdmin.useMutation({});

  const documentDeleteReasonRequired =
    documentRestoreMode.includes('reason_required');

  const [
    deleteSettingsModal,
    showDeleteSettingsModal,
    hideDeleteSettingsModal,
  ] = useModal({
    title: intl.formatMessage({
      id: 'RestorationHub.deletedDocumentsModalTitle',
      defaultMessage: 'Deleted Documents',
      description: 'Title for deleted documents settings modal',
    }),
    children: (
      <CheckboxGroup
        name="deleted-documents"
        value={documentRestoreMode}
        options={[
          {
            label: intl.formatMessage({
              id: 'RestorationHub.requireReasonCheckboxLabel',
              defaultMessage:
                'Require users to input a reason when deleting a document',
              description: 'Label for checkbox to require deletion reason',
            }),
            value: 'reason_required',
          },
        ]}
        onChange={(mode) => {
          setDocumentRestoreMode(mode ? mode : []);
        }}
      />
    ),
    primaryAction: {
      text: intl.formatMessage({
        id: 'RestorationHub.saveChangesButton',
        defaultMessage: 'Save Changes',
        description: 'Button text to save changes',
      }),
      onClick: () => {
        updateClientAdmin({
          clientId: clientId!,
          documentDeleteReasonRequired,
        })
          .then(() => {
            toast({
              message: intl.formatMessage(
                {
                  id: 'RestorationHub.requireReasonSuccessMessage',
                  defaultMessage:
                    'Requiring users to input reason when deleting a document has been {status}.',
                  description:
                    'Success message for enabling/disabling deletion reason requirement. Example: status = "enabled"',
                },
                {
                  status: documentDeleteReasonRequired
                    ? intl.formatMessage({
                        id: 'RestorationHub.enabled',
                        defaultMessage: 'enabled',
                        description: 'Status when feature is enabled',
                      })
                    : intl.formatMessage({
                        id: 'RestorationHub.disabled',
                        defaultMessage: 'disabled',
                        description: 'Status when feature is disabled',
                      }),
                },
              ),
              status: 'success',
            });
            hideDeleteSettingsModal();
          })
          .catch(() => {
            toast({
              message: documentDeleteReasonRequired
                ? intl.formatMessage({
                    id: 'RestorationHub.requireReasonEnableError',
                    defaultMessage:
                      'An error occurred while enabling requirement for users to input a reason when deleting a document.',
                    description:
                      'Error message when enabling deletion reason requirement fails',
                  })
                : intl.formatMessage({
                    id: 'RestorationHub.requireReasonDisableError',
                    defaultMessage:
                      'An error occurred while disabling requirement for users to input a reason when deleting a document.',
                    description:
                      'Error message when disabling deletion reason requirement fails',
                  }),
              status: 'danger',
            });
          });
      },
      variant: 'primary',
      isLoading: isUpdatingSettings,
    },
    onCancel: () => {
      hideDeleteSettingsModal();
    },
  });

  const deletePermanentlyEndpoint = isAdminConsole
    ? api.endpoints.permanentlyDeleteDocuments
    : api.endpoints.permanentlyDeleteDocumentsForCurrentUser;
  const [deletePermanently, deletePermanentlyResult] =
    deletePermanentlyEndpoint.useMutation();
  const {
    isError: isDeletePermanentlyError,
    isSuccess: isDeletePermanentlySuccess,
  } = deletePermanentlyResult;

  const restoreDocumentsEndpoint = isAdminConsole
    ? api.endpoints.restoreDeletedDocuments
    : api.endpoints.restoreDeletedDocumentsForCurrentUser;
  const [restoreDocuments, restoreDocumentsResult] =
    restoreDocumentsEndpoint.useMutation();
  const {
    isError: isRestoreDocumentsError,
    isSuccess: isRestoreDocumentsSuccess,
  } = restoreDocumentsResult;

  const tableContext = isAdminConsole
    ? TableContextType.RestorationHub
    : TableContextType.MyDeletedItems;
  const { tableSettings } = useTableSettings(tableContext);

  useEffect(() => {
    if (tableSettings) {
      setQuery({
        ...query,
        pageSize: tableSettings.pageSize,
        sort: tableSettings.sortBy,
      });
      setColumnOrder(tableSettings.columnOrder);
    }
  }, [tableSettings]);

  const onRowRestoreClick = (data: DeletedDocumentResult) => {
    setSelectedDocuments([data]);
    setShowRestoreModal(true);
  };

  const onBulkRestoreClick = async () => {
    if (selectedRowIds.length && !allSelected) {
      const allData = await getAllData();
      if (allData) {
        const selectedData = allData.filter((item) =>
          selectedRowIds.includes(`${item.id}`),
        );
        setSelectedDocuments(selectedData);
      }
    }
    setShowRestoreModal(true);
    showRestoreExceedsLimitModal();
  };

  const onRowDeleteClick = (data: DeletedDocumentResult) => {
    setSelectedDocuments([data]);
    setShowPermanentDeleteModal(true);
  };

  const onBulkDeleteClick = async () => {
    if (selectedRowIds.length && !allSelected) {
      const allData = await getAllData();
      if (allData) {
        const selectedData = allData.filter((item) =>
          selectedRowIds.includes(`${item.id}`),
        );
        setSelectedDocuments(selectedData);
      }
    }
    setShowPermanentDeleteModal(true);
    showDeleteExceedsLimitModal();
  };

  const onRowDownloadClick = (data: DeletedDocumentResult) => {
    const downloadDeletedDocumentEndpoint = isAdminConsole
      ? downloadDeletedDocument
      : downloadDeletedDocumentForCurrentUser;
    const displayError = () =>
      showToast(
        ERROR,
        intl.formatMessage(
          {
            id: 'RestorationHub.downloadFailedMessage',
            defaultMessage: 'Failed to download "{fileName}"',
            description:
              'Error message when document download fails. Example: fileName = "contract.pdf"',
          },
          { fileName: data.name },
        ),
      );

    downloadDeletedDocumentEndpoint(data.id)
      .then((response: any) =>
        downloadFile(response, `${data.name}${data.fileType}`),
      )
      .catch(displayError);
  };

  const actions = [
    {
      key: 'restore',
      icon: 'restore',
      label: intl.formatMessage({
        id: 'RestorationHub.restoreLabel',
        defaultMessage: 'Restore',
        description: 'Label for restore action',
      }),
      onClick: () => {},
    },
    {
      key: 'download',
      icon: 'download',
      label: intl.formatMessage({
        id: 'RestorationHub.downloadLabel',
        defaultMessage: 'Download',
        description: 'Label for download action',
      }),
      onClick: () => {},
    },
    {
      key: 'delete',
      icon: 'trash-permanent',
      label: intl.formatMessage({
        id: 'RestorationHub.deletePermanentlyLabel',
        defaultMessage: 'Delete permanently',
        description: 'Label for permanent delete action',
      }),
      onClick: () => {},
    },
  ];

  const rowActions = actions.map((action) => {
    switch (action.key) {
      case 'restore':
        return { ...action, onClick: onRowRestoreClick };
      case 'delete':
        return { ...action, onClick: onRowDeleteClick };
      case 'download':
        return { ...action, onClick: onRowDownloadClick };
      default:
        return action;
    }
  });

  const bulkActions = actions
    .filter((action) => action.key !== 'download')
    .map((action) => {
      switch (action.key) {
        case 'restore':
          return { ...action, onClick: onBulkRestoreClick };
        case 'delete':
          return { ...action, onClick: onBulkDeleteClick };
        default:
          return action;
      }
    });

  const columns = [
    {
      key: 'name',
      cellType: 'filesystem',
      title: intl.formatMessage({
        id: 'RestorationHub.nameColumnTitle',
        defaultMessage: 'Name',
        description: 'Column title for document name',
      }),
      sortType: (rowA: Row, rowB: Row, columnId: string) =>
        sortStrings(rowA.values[columnId], rowB.values[columnId]),
      mapCellProps: (rowData: DeletedDocumentResult) => {
        return {
          file: {
            name: `${rowData.name}${rowData.fileType}`,
            includeExtension: false,
          },
        };
      },
    },
    {
      key: 'original_folder',
      cellType: 'filesystem',
      title: intl.formatMessage({
        id: 'RestorationHub.originalFolderColumnTitle',
        defaultMessage: 'Original Folder',
        description: 'Column title for original folder',
      }),
      mapCellProps: (rowData: DeletedDocumentResult) => {
        return {
          folder: {
            name: rowData.originalFolder,
            path: rowData.originalFolder,
          },
        };
      },
    },
    {
      key: 'deleted_date',
      cellType: 'datetime',
      title: intl.formatMessage({
        id: 'RestorationHub.dateDeletedColumnTitle',
        defaultMessage: 'Date Deleted',
        description: 'Column title for deletion date',
      }),
      minWidth: 'm',
      width: 'm',
      mapCellProps: ({ deletedDate }: DeletedDocumentResult) => {
        return {
          datetime: deletedDate,
        };
      },
    },
    {
      key: 'reason',
      cellType: 'text',
      title: intl.formatMessage({
        id: 'RestorationHub.reasonDeletedColumnTitle',
        defaultMessage: 'Reason Deleted',
        description: 'Column title for deletion reason',
      }),
      mapCellProps: (rowData: DeletedDocumentResult) => {
        return {
          text: displayReason(rowData.reason),
          description: rowData.extraDetails,
          shouldTruncate: true,
        };
      },
    },
    {
      key: 'deleted_by',
      cellType: 'user',
      title: intl.formatMessage({
        id: 'RestorationHub.deletedByColumnTitle',
        defaultMessage: 'Deleted By',
        description: 'Column title for user who deleted',
      }),
      mapCellProps: (rowData: DeletedDocumentResult) => ({
        asyncUser: {
          id: rowData.deletedById,
          render: UserComponent,
        },
        mode: 'avatar-name',
      }),
    },
  ];

  const handleCancelRestore = () => {
    setSelectedFolder(0);
    setShowRestoreModal(false);
    hideRestoreExceedsLimitModal();
  };

  const handleCancelPermanentDelete = () => {
    setSelectedDocuments([]);
    setShowPermanentDeleteModal(false);
    hideDeleteExceedsLimitModal();
  };

  const count = allSelected
    ? tableData!.count
    : selectedRowIds.length || selectedDocuments.length || 0;
  const documentIds = selectedDocuments.map((document) => document.id);

  const handlePermanentlyDeleteDocuments = () => {
    deletePermanently({ documentIds, allSelected, filters: query.filters });

    setShowPermanentDeleteModal(false);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setQuery({ ...query, page: PAGE_START, pageSize: newPageSize });
  };

  const handlePaginate = ({ pageIndex }: { pageIndex: number }) => {
    setQuery({ ...query, page: pageIndex });
  };

  const handleTableUpdate = (
    {
      isAllRowsSelected,
      selectedRowIds: updatedSelectedRowIds,
      sortBy,
    }: {
      isAllRowsSelected: boolean;
      selectedRowIds: string[];
      sortBy: { id: string; desc: boolean }[];
    },
    action?: ActionType,
  ) => {
    if (action?.type === 'toggleRowSelected') {
      setSelectedRowIds(updatedSelectedRowIds);
    }

    if (action?.type === 'toggleAllPageRowsSelected') {
      if (updatedSelectedRowIds.length > selectedRowIds.length) {
        setSelectedRowIds(union(selectedRowIds, updatedSelectedRowIds));
      } else {
        setSelectedRowIds(updatedSelectedRowIds);
      }
    }

    if (action?.type === 'toggleAllRowsSelected') {
      setAllSelected(isAllRowsSelected);
      if (!isAllRowsSelected) {
        setSelectedRowIds([]);
      }
    }

    if (action?.type === 'toggleMultipleRowsSelected') {
      setSelectedRowIds(union(selectedRowIds, updatedSelectedRowIds));
    }

    if (action?.type === 'toggleSortBy') {
      setQuery({ ...query, sort: sortBy[0] });
    }
  };

  const handleSetQuery = (query: TableQuery) => {
    if (isAdminConsole) {
      setQuery(query);
    } else {
      setQuery({
        ...query,
        filters: { ...query.filters, deletedBy: [currentUser.email] },
      });
    }
  };

  const setColumnOrder = (columnOrder: string[]) => {
    if (isAdminConsole) {
      dispatch(slices.restorationHub.actions.setColumnOrder(columnOrder));
    } else {
      dispatch(slices.myDeletedItems.actions.setColumnOrder(columnOrder));
    }
  };

  const resetPageIfEmpty = () => {
    const oldCount = tableData?.count ?? 0;
    if (count === oldCount) {
      // If all documents were removed, reset the filters & page index
      setQuery({ ...query, page: PAGE_START, filters: {} });
    } else {
      const newCount = oldCount > count ? oldCount - count : 0;
      const newLastPage =
        newCount > 0 ? Math.ceil(newCount / query.pageSize) : PAGE_START;
      if (query.page > newLastPage) {
        // If the current page is now empty, go to the new last page
        setQuery({ ...query, page: newLastPage });
      }
    }
  };

  useEffect(() => {
    if (isDeletePermanentlySuccess) {
      showToast(
        SUCCESS,
        intl.formatMessage(
          {
            id: 'RestorationHub.permanentDeleteSuccessMessage',
            defaultMessage:
              '{count, plural, =0 {No documents} one {# document} other {# documents}} permanently deleted.',
            description:
              'Success message when documents are permanently deleted. Example: count, plural, =0 {No documents = "3", # document = "example value", # documents = "example value", count = "3"',
          },
          {
            count,
          },
        ),
      );
      setSelectedRowIds([]);
      setSelectedDocuments([]);
      setShowPermanentDeleteModal(false);
      resetPageIfEmpty();
    }

    if (isDeletePermanentlyError) {
      showToast(
        ERROR,
        intl.formatMessage(
          {
            id: 'RestorationHub.permanentDeleteErrorMessage',
            defaultMessage:
              'An error occurred: {count, plural, one {# document was} other {# documents were}} not deleted.',
            description:
              'Error message when permanent deletion fails. Example: count = 3',
          },
          {
            count,
          },
        ),
      );
      setShowPermanentDeleteModal(false);
    }
  }, [isDeletePermanentlySuccess, isDeletePermanentlyError]);

  useEffect(() => {
    if (isRestoreDocumentsSuccess) {
      showToast(
        SUCCESS,
        intl.formatMessage(
          {
            id: 'RestorationHub.restoreSuccessMessage',
            defaultMessage:
              '{count, plural, =0 {No documents} one {# document} other {# documents}} restored.',
            description:
              'Success message when documents are restored. Example: count = 5',
          },
          {
            count,
          },
        ),
      );
      setSelectedRowIds([]);
      setSelectedDocuments([]);
      resetPageIfEmpty();
    }

    if (isRestoreDocumentsError) {
      showToast(
        ERROR,
        intl.formatMessage(
          {
            id: 'RestorationHub.restoreErrorMessage',
            defaultMessage:
              'An error occurred: {count, plural, one {# document was} other {# documents were}} not restored.',
            description:
              'Error message when restoration fails. Example: count = 3',
          },
          {
            count,
          },
        ),
      );
    }
  }, [isRestoreDocumentsSuccess, isRestoreDocumentsError]);

  const restoreDocumentToFolder = () => {
    restoreDocuments({
      documentIds,
      allSelected,
      folderId: selectedFolder,
      filters: query.filters,
    });
    setShowRestoreModal(false);
  };

  const renderFolderTreeRestoreModal = () => {
    return (
      <>
        {!folderError && !!folderData && (
          <FolderModal
            title={intl.formatMessage({
              id: 'RestorationHub.selectFolderModalTitle',
              defaultMessage: 'Select the folder to restore to',
              description: 'Title for folder selection modal',
            })}
            onSelectFolder={restoreDocumentToFolder}
            onHide={handleCancelRestore}
            visible={showRestoreModal}
            folderTreeProps={{
              label: '',
              folders: [folderData],
              onChange: setSelectedFolder,
              value: selectedFolder,
            }}
            disabled={!selectedFolder || (count || 0) > BULK_RESTORE_LIMIT}
            modalWidth="m"
            count={count}
          />
        )}
      </>
    );
  };

  const [
    restoreExceedsLimitModal,
    showRestoreExceedsLimitModal,
    hideRestoreExceedsLimitModal,
  ] = useLimitExceededModal({
    limit: BULK_RESTORE_LIMIT,
    count,
    actionType: 'restore',
    primaryActionHide: handleCancelRestore,
  });

  const [
    deleteExceedsLimitModal,
    showDeleteExceedsLimitModal,
    hideDeleteExceedsLimitModal,
  ] = useLimitExceededModal({
    limit: BULK_DELETE_LIMIT,
    count,
    actionType: 'permanentlyDelete',
    primaryActionHide: handleCancelPermanentDelete,
  });

  const renderConfirmPermanentDeleteModal = () => {
    return (
      <DeleteDocumentPermanentlyModal
        selectedDocuments={selectedDocuments}
        isVisible={showPermanentDeleteModal}
        totalCount={count}
        onConfirm={handlePermanentlyDeleteDocuments}
        onCancel={handleCancelPermanentDelete}
      />
    );
  };

  const nav = generateNav({
    current: {
      text: intl.formatMessage({
        id: 'RestorationHub.navigationText',
        defaultMessage: 'Restoration Hub',
        description: 'Navigation breadcrumb text for restoration hub',
      }),
      pathname: RoutePathType.AdminConsoleClientRestorationHub,
    },
    from: RoutePathType.AdminConsoleClient,
    params: {
      clientId: currentUser.client,
    },
    intl,
  });

  const getAllData = async (isExport?: boolean) => {
    const endpoint = isAdminConsole
      ? getDeletedDocuments
      : getDeletedDocumentsForCurrentUser;
    const allQuery = { ...query, page: 1, pageSize: BULK_EXPORT_LIMIT };
    try {
      const response = await endpoint(allQuery);
      return response.results;
    } catch {
      let message = intl.formatMessage({
        id: 'RestorationHub.generalErrorMessage',
        defaultMessage:
          'An error occurred. Please try again later or contact support.',
        description: 'General error message',
      });
      if (isExport) {
        message = intl.formatMessage({
          id: 'RestorationHub.exportFailedMessage',
          defaultMessage:
            'Export failed. Please try again later or contact support.',
          description: 'Error message when export fails',
        });
      }
      showToast(ERROR, message);
    }
  };

  const handleExport = async ({ columnOrder }: { columnOrder: string[] }) => {
    const allData = await getAllData(true);
    if (allData) {
      if (selectedRowIds.length && !allSelected) {
        const selectedData = allData.filter((item) =>
          selectedRowIds.includes(`${item.id}`),
        );
        if (selectedData.length) {
          await exportXlsx({
            data: getExportDataFromColumns(selectedData, columnOrder),
            name: 'Deleted Documents',
          });
        }
      } else {
        await exportXlsx({
          data: getExportDataFromColumns(allData, columnOrder),
          name: 'Deleted Documents',
        });
      }
    }
  };

  const pageTitle = isAdminConsole
    ? intl.formatMessage({
        id: 'RestorationHub.restorationHubTitle',
        defaultMessage: 'Restoration Hub',
        description: 'Page title for restoration hub',
      })
    : intl.formatMessage({
        id: 'RestorationHub.myDeletedItemsTitle',
        defaultMessage: 'My Deleted Items',
        description: 'Page title for my deleted items',
      });

  const deletedBy = isAdminConsole
    ? intl.formatMessage({
        id: 'RestorationHub.anyUser',
        defaultMessage: 'any user',
        description: 'Text for any user context',
      })
    : intl.formatMessage({
        id: 'RestorationHub.you',
        defaultMessage: 'you',
        description: 'Text for current user context',
      });

  const message = intl.formatMessage(
    {
      id: 'RestorationHub.pageDescription',
      defaultMessage:
        'Here you can find files that were deleted recently by {deletedBy}. Files may be permanently deleted from here 90 days after their deletion from the platform. Once a file is permanently deleted it cannot be restored.',
      description:
        'Description text explaining the restoration hub functionality. Example: deletedBy = "John Doe"',
    },
    { deletedBy },
  );

  return (
    <Layout direction="column" spacing={8}>
      <PageLayout
        title={pageTitle}
        nav={nav}
        moreActions={
          checkPermission('deleted_documents', 'view') && isAdminConsole
            ? [
                {
                  id: 'restore-settings',
                  text: intl.formatMessage({
                    id: 'RestorationHub.deletedDocumentSettings',
                    defaultMessage: 'Deleted Document Settings',
                    description: 'Action text for deleted document settings',
                  }),
                  onClick: () => {
                    showDeleteSettingsModal();
                  },
                },
              ]
            : []
        }
      />
      <Alert enableIcon variant="info" key="info-message">
        {message}
      </Alert>
      <DocumentsRestoreFilters
        query={query}
        onChange={handleSetQuery}
        isAdminConsole={isAdminConsole}
      />
      <>
        <ContentContainer
          loadingContent={{
            isLoading: isLoading && !tableData,
            message: intl.formatMessage(
              {
                id: 'RestorationHub.loadingMessage',
                defaultMessage: 'Loading {pageTitle}',
                description:
                  'Loading message for restoration hub. Example: pageTitle = "Restoration Hub"',
              },
              { pageTitle },
            ),
          }}
        />
        {!error && !!tableData && !!tableData.results.length && (
          <PersistedTable
            context={tableContext}
            actions={[
              {
                key: 'export',
                icon: 'download',
                label: intl.formatMessage({
                  id: 'RestorationHub.exportExcelLabel',
                  defaultMessage: 'Export Excel',
                  description: 'Label for export excel action',
                }),
                onClick: handleExport,
              },
            ]}
            columns={columns}
            data={tableData.results}
            isLoading={isLoading}
            name={pageTitle}
            options={{
              enableExportXlsx: false,
              enablePageSizeSelect: true,
              pageSizes: RESTORATION_HUB_PAGE_SIZE_LIST,
            }}
            rowActions={rowActions}
            bulkActions={bulkActions}
            state={{
              columnOrder,
              pageIndex: query.page,
              pageSize: query.pageSize,
              sortBy: [query.sort],
              selectedRowIds,
              isAllRowsSelected: allSelected,
            }}
            reactTableOptions={{
              autoResetSortBy: false,
              disableSortRemove: true,
              manualSortBy: true,
            }}
            totalCount={tableData.count}
            onPageSizeChange={handlePageSizeChange}
            onUpdate={handleTableUpdate}
            onPaginate={handlePaginate}
            onSetColumnOrder={setColumnOrder}
          />
        )}
        {!error && !!tableData && !tableData.results.length && (
          <Layout
            direction="column"
            spacing={8}
            maxW="516px"
            align="center"
            m="auto"
          >
            <FolderEmptyIcon />
            <Text textAlign="center">{message}</Text>
          </Layout>
        )}
      </>
      {count > BULK_RESTORE_LIMIT
        ? restoreExceedsLimitModal
        : renderFolderTreeRestoreModal()}
      {count > BULK_DELETE_LIMIT
        ? deleteExceedsLimitModal
        : renderConfirmPermanentDeleteModal()}
      {deleteSettingsModal}
    </Layout>
  );
};

export default withPermissions(withCurrentUser(Page), PERMISSIONS);
